import { useEffect, useState } from 'react'
import {
  fetchCompetitors, fetchScrapeRuns, fetchAudit, triggerScrape, refreshSpot,
  patchCompetitor, deleteCompetitor, createCompetitor, fetchCompetitorProfile,
  type Competitor, type ScrapeRun, type AuditProduct,
} from '../lib/api'
import { Card, SectionTitle, EmptyState, TierBadge } from '../components/ui'
import { aud, metalLabel } from '../lib/format'

export default function Admin() {
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [runs, setRuns] = useState<ScrapeRun[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const reload = () => {
    fetchCompetitors().then((r) => setCompetitors(r.competitors)).catch(() => {})
    fetchScrapeRuns().then((r) => setRuns(r.runs)).catch(() => {})
  }
  useEffect(reload, [])

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 4000) }

  const onScrape = async (c: Competitor) => {
    setBusy(`scrape-${c.id}`)
    try {
      const r = await triggerScrape(c.id)
      flash(r.ok ? `Scraped ${c.name}: ${JSON.stringify(r.summary)}` : `Scrape failed: ${r.error}`)
    } catch (e) { flash(`Scrape error: ${e}`) } finally { setBusy(null); reload() }
  }
  const onDelete = async (c: Competitor) => {
    if (!confirm(`Delete ${c.name} and all its data?`)) return
    await deleteCompetitor(c.id); flash(`Deleted ${c.name}`); reload()
  }

  return (
    <div className="space-y-8">
      {msg && <div className="rounded-md border border-gold/40 bg-gold/10 px-4 py-2 font-body text-sm text-oxblood">{msg}</div>}

      <SpotPanel onDone={flash} />

      {/* Competitor management */}
      <section>
        <SectionTitle note={`${competitors.length} competitors`}>Competitors</SectionTitle>
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse font-body text-sm">
            <thead className="border-b border-oxblood/10 bg-blush/20">
              <tr>{['Name', 'Tier', 'Products', 'Variants', 'Actions'].map((h) => (
                <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wide text-oxblood/60">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {competitors.map((c) => (
                <tr key={c.id} className="border-b border-oxblood/5">
                  <td className="px-3 py-2 text-ink">{c.name}<span className="ml-2 text-xs text-ink/30">{c.website_url}</span></td>
                  <td className="px-3 py-2"><TierBadge tier={c.positioning_tier} /></td>
                  <td className="px-3 py-2 tabular-nums text-ink/70">{c.product_count}</td>
                  <td className="px-3 py-2 tabular-nums text-ink/70">{c.variant_count}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button disabled={busy === `scrape-${c.id}`} onClick={() => onScrape(c)}
                        className="rounded border border-oxblood/20 px-2 py-1 text-xs text-oxblood hover:bg-oxblood/5 disabled:opacity-40">
                        {busy === `scrape-${c.id}` ? 'Scraping…' : 'Run scrape'}
                      </button>
                      <ConfigEditor competitor={c} onSaved={reload} />
                      <button onClick={() => onDelete(c)} className="rounded border border-oxblood/20 px-2 py-1 text-xs text-oxblood/70 hover:bg-oxblood/5">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <AddCompetitor onAdded={() => { flash('Competitor added'); reload() }} />
      </section>

      {/* Scrape run log */}
      <section>
        <SectionTitle note="most recent first">Scrape run log</SectionTitle>
        <Card className="overflow-x-auto">
          {runs.length ? (
            <table className="w-full min-w-[720px] border-collapse font-body text-sm">
              <thead className="border-b border-oxblood/10 bg-blush/20">
                <tr>{['Competitor', 'Started', 'Status', 'Pages', 'Found', 'New', 'Errors'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wide text-oxblood/60">{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const errs = r.errors ? (JSON.parse(r.errors) as unknown[]).length : 0
                  return (
                    <tr key={r.id} className="border-b border-oxblood/5">
                      <td className="px-3 py-2 text-ink">{r.competitor_name ?? '—'}</td>
                      <td className="px-3 py-2 text-ink/60">{r.started_at}</td>
                      <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                      <td className="px-3 py-2 tabular-nums text-ink/70">{r.pages_fetched}</td>
                      <td className="px-3 py-2 tabular-nums text-ink/70">{r.products_found}</td>
                      <td className="px-3 py-2 tabular-nums text-ink/70">{r.products_new}</td>
                      <td className={`px-3 py-2 tabular-nums ${errs ? 'text-oxblood' : 'text-ink/30'}`}>{errs}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : <EmptyState title="No scrape runs yet" hint="Trigger a scrape above; every run logs here with page counts and errors." />}
        </Card>
      </section>

      <AuditPanel competitors={competitors} />
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const s: Record<string, string> = {
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    running: 'bg-blue-50 text-blue-700 border-blue-200',
  }
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] ${s[status] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>{status}</span>
}

function SpotPanel({ onDone }: { onDone: (m: string) => void }) {
  const [gold, setGold] = useState(''); const [plat, setPlat] = useState('')
  const [busy, setBusy] = useState(false)
  const live = async () => {
    setBusy(true)
    try { const r = await refreshSpot({}); onDone(r.ok ? `Spot updated: ${JSON.stringify(r.latest)}` : `Spot failed: ${r.error}`) }
    catch (e) { onDone(`Spot error: ${e}`) } finally { setBusy(false) }
  }
  const manual = async () => {
    const body: Record<string, number> = {}
    if (gold) body.gold = parseFloat(gold); if (plat) body.platinum = parseFloat(plat)
    const r = await refreshSpot({ manual: body })
    onDone(r.ok ? 'Manual spot saved' : `Failed: ${r.error}`)
  }
  return (
    <section>
      <SectionTitle note="captured each cycle · Swissquote (keyless + live FX), Kitco / metals.dev fallback">Spot prices</SectionTitle>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <button disabled={busy} onClick={live} className="rounded-md bg-oxblood px-4 py-2 font-body text-sm text-offwhite hover:bg-oxblood/90 disabled:opacity-40">
          {busy ? 'Fetching…' : 'Refresh from provider'}
        </button>
        <span className="font-body text-xs text-ink/40">or set manually (AUD/gram):</span>
        <input value={gold} onChange={(e) => setGold(e.target.value)} placeholder="gold /g" className="w-24 rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm" />
        <input value={plat} onChange={(e) => setPlat(e.target.value)} placeholder="platinum /g" className="w-28 rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm" />
        <button onClick={manual} className="rounded-md border border-oxblood/20 px-4 py-2 font-body text-sm text-oxblood hover:bg-oxblood/5">Save manual</button>
      </Card>
    </section>
  )
}

function ConfigEditor({ competitor, onSaved }: { competitor: Competitor; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const DEFAULT_CONFIG = '{\n  "product_urls": [],\n  "is_custom": false,\n  "prefer_shopify": true\n}'
  const load = async () => {
    setText('Loading…'); setOpen(true)
    try {
      const p = await fetchCompetitorProfile(competitor.id)
      const cfg = p.competitor.scrape_config
      setText(cfg ? JSON.stringify(JSON.parse(cfg), null, 2) : DEFAULT_CONFIG)
    } catch {
      setText(DEFAULT_CONFIG)
    }
  }
  const save = async () => {
    try { JSON.parse(text) } catch { alert('Invalid JSON'); return }
    await patchCompetitor(competitor.id, { scrape_config: text })
    setOpen(false); onSaved()
  }
  return (
    <>
      <button onClick={load} className="rounded border border-oxblood/20 px-2 py-1 text-xs text-oxblood hover:bg-oxblood/5">Config</button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-oxblood">{competitor.name} — scrape config</h3>
            <p className="mb-2 font-body text-xs text-ink/40">product_urls, is_custom, prefer_shopify, max_pages</p>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12}
              className="w-full rounded-md border border-oxblood/15 px-3 py-2 font-mono text-xs" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="rounded-md border border-oxblood/20 px-4 py-2 font-body text-sm text-oxblood/70">Cancel</button>
              <button onClick={save} className="rounded-md bg-oxblood px-5 py-2 font-body text-sm text-offwhite">Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function AddCompetitor({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState(''); const [url, setUrl] = useState(''); const [tier, setTier] = useState('mid')
  const add = async () => {
    if (!name || !url) return
    await createCompetitor({ name, website_url: url, positioning_tier: tier, scrape_config: { product_urls: [], prefer_shopify: true } })
    setName(''); setUrl(''); onAdded()
  }
  return (
    <Card className="mt-3 flex flex-wrap items-end gap-3 p-4">
      <span className="font-body text-xs uppercase tracking-wide text-oxblood/50">Add competitor</span>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm" />
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="w-64 rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm" />
      <select value={tier} onChange={(e) => setTier(e.target.value)} className="rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm">
        {['budget', 'mid', 'premium', 'bespoke'].map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <button onClick={add} className="rounded-md bg-oxblood px-4 py-2 font-body text-sm text-offwhite hover:bg-oxblood/90">Add</button>
    </Card>
  )
}

function AuditPanel({ competitors }: { competitors: Competitor[] }) {
  const [cid, setCid] = useState<number | ''>('')
  const [products, setProducts] = useState<AuditProduct[] | null>(null)
  useEffect(() => {
    if (cid === '') { setProducts(null); return }
    fetchAudit(Number(cid)).then((r) => setProducts(r.products)).catch(() => setProducts([]))
  }, [cid])

  return (
    <section>
      <SectionTitle note="raw source vs extracted JSON — spot-check extraction accuracy">Extraction audit</SectionTitle>
      <Card className="p-4">
        <select value={cid} onChange={(e) => setCid(e.target.value ? Number(e.target.value) : '')}
          className="mb-4 rounded-md border border-oxblood/15 px-2 py-1.5 font-body text-sm">
          <option value="">Select a competitor…</option>
          {competitors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {cid === '' ? null : !products ? <p className="font-body text-sm text-ink/40">Loading…</p> :
          products.length === 0 ? <EmptyState title="No scraped products for this competitor yet" hint="Run a scrape; the source markdown / product.json and the extracted variants will appear here side by side." /> : (
            <div className="space-y-4">
              {products.slice(0, 10).map((p) => (
                <div key={p.id} className="rounded-md border border-oxblood/10">
                  <div className="flex items-center justify-between border-b border-oxblood/10 bg-blush/20 px-3 py-2">
                    <span className="font-body text-sm text-oxblood">{p.product_name ?? p.source_url}</span>
                    <span className="rounded bg-oxblood/5 px-2 py-0.5 font-body text-[11px] text-oxblood/60">{p.extraction_method ?? '—'}</span>
                  </div>
                  <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
                    <div className="border-r border-oxblood/10 p-3">
                      <p className="mb-1 font-body text-[11px] uppercase tracking-wide text-ink/40">Raw source</p>
                      <pre className="max-h-72 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-ink/70">{p.raw_source ?? '(not stored)'}</pre>
                    </div>
                    <div className="p-3">
                      <p className="mb-1 font-body text-[11px] uppercase tracking-wide text-ink/40">Extracted variants ({p.variants.length})</p>
                      <div className="max-h-72 overflow-auto">
                        <table className="w-full border-collapse font-body text-xs">
                          <tbody>
                            {p.variants.map((v, i) => (
                              <tr key={i} className="border-b border-oxblood/5">
                                <td className="py-1 pr-2 text-ink/80">{metalLabel(v.metal)}</td>
                                <td className="py-1 pr-2 tabular-nums text-ink/60">{v.band_width_mm ? `${v.band_width_mm}mm` : '—'}</td>
                                <td className="py-1 text-right tabular-nums text-ink">{v.price_aud != null ? aud(v.price_aud, { round: true }) : v.price_type}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Card>
    </section>
  )
}
